import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import "./LocationHistory.css";

const BASE_URL = process.env.REACT_APP_API_URL || "https://backend.whereisfran.com";
const HISTORY_URL = `${BASE_URL}/location/history`;

// Display names keyed by email address
const USER_NAMES = {
  "john.milsom01@gmail.com": "John",
  "liamdcunliffe@gmail.com": "Liam",
  "trevorgiles@gmail.com": "Trevor",
};

function formatTimestamp(timestamp) {
  if (!timestamp) return "—";
  try {
    const date = timestamp._seconds
      ? new Date(timestamp._seconds * 1000)
      : new Date(timestamp);
    return date.toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return String(timestamp);
  }
}

function formatName(email) {
  if (!email) return "Unknown";
  return USER_NAMES[email] || email;
}

function LocationHistory() {
  const { idToken } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Stack of cursors: index 0 = first page (no cursor), each subsequent entry
  // is the nextCursor returned by the previous page fetch.
  const [cursorStack, setCursorStack] = useState([null]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);

  const fetchPage = useCallback(
    (cursor) => {
      if (!idToken) return;
      setLoading(true);
      setError(null);
      const params = cursor ? { startAfter: cursor } : {};
      axios
        .get(HISTORY_URL, {
          headers: { Authorization: `Bearer ${idToken}` },
          params,
        })
        .then((res) => {
          setItems(res.data.items || []);
          setNextCursor(res.data.nextCursor || null);
        })
        .catch((err) => {
          console.error("Error fetching location history:", err);
          setError("Failed to load history. Please try again.");
        })
        .finally(() => setLoading(false));
    },
    [idToken]
  );

  // Load first page on mount / token change
  useEffect(() => {
    setCursorStack([null]);
    setCurrentPageIndex(0);
    setNextCursor(null);
    fetchPage(null);
  }, [fetchPage]);

  const handleNext = () => {
    if (!nextCursor) return;
    const newStack = [...cursorStack.slice(0, currentPageIndex + 1), nextCursor];
    setCursorStack(newStack);
    setCurrentPageIndex(currentPageIndex + 1);
    fetchPage(nextCursor);
  };

  const handlePrev = () => {
    if (currentPageIndex === 0) return;
    const prevIndex = currentPageIndex - 1;
    setCurrentPageIndex(prevIndex);
    fetchPage(cursorStack[prevIndex]);
  };

  const isFirstPage = currentPageIndex === 0;
  const hasNextPage = !!nextCursor;

  return (
    <div className="history-container">
      <h2 className="history-title">Location History</h2>

      {error && <div className="history-error">{error}</div>}

      {loading ? (
        <div className="history-loading">Loading…</div>
      ) : (
        <>
          {items.length === 0 ? (
            <div className="history-empty">No history recorded yet.</div>
          ) : (
            <ul className="history-list">
              {items.map((item) => (
                <li key={item.id} className="history-item">
                  <div className="history-item-time">
                    {formatTimestamp(item.timestamp)}
                  </div>
                  <div className="history-item-meta">
                    <span className="history-item-who">
                      {formatName(item.updatedBy)}
                    </span>
                    <span className="history-item-coords">
                      {item.lat?.toFixed(5)}, {item.lng?.toFixed(5)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="history-pagination">
            <button
              className="history-page-btn"
              onClick={handlePrev}
              disabled={isFirstPage || loading}
            >
              ← Prev
            </button>
            <span className="history-page-label">Page {currentPageIndex + 1}</span>
            <button
              className="history-page-btn"
              onClick={handleNext}
              disabled={!hasNextPage || loading}
            >
              Next →
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default LocationHistory;
